import { leaseMonthlyDisplay } from "../../shared/leaseMonthlyDisplay.js";

// Read-only headline contract. Schedule comparison rates are deliberately
// separate: an annual calendar-year total must never be reconstructed from them.
export function annualAreaBasis(lease, year, row) {
  if (!row || row.isNotInSource || row.isFallback || Number(row.months ?? 12) !== 12) return null;
  const from = `${year}-01-01`;
  const to = `${year}-12-31`;
  const iso = (value) => value instanceof Date
    ? value.toISOString().slice(0, 10) : value ? String(value).slice(0, 10) : null;
  const leaseStart = iso(lease?.startDate);
  const leaseEnd = iso(lease?.endDate);
  if ((leaseStart && leaseStart > from) || (leaseEnd && leaseEnd < to)) return null;
  const areas = Array.isArray(lease?.areas) ? lease.areas : [];
  const overlaps = areas.filter((a) => {
    const start = iso(a.commencementDate);
    const end = iso(a.expirationDate);
    return (!start || start <= to) && (!end || end >= from);
  });
  // A dated change within the year invalidates even a generic annual footprint.
  if (overlaps.some((a) => {
    const start = iso(a.commencementDate);
    const end = iso(a.expirationDate);
    return (start && start > from) || (end && end < to);
  })) return null;
  const yearCost = lease?.yearCosts?.find((yc) => Number(yc.year) === Number(year) && !yc.isProjected);
  const recorded = Number(yearCost?.premisesSqft);
  if (Number.isFinite(recorded) && recorded > 0) return recorded;
  // Otherwise every contributing suite needs a positive, dated full-year basis.
  if (overlaps.length > 0 && overlaps.every((a) =>
    iso(a.commencementDate) && iso(a.commencementDate) <= from &&
    iso(a.expirationDate) && iso(a.expirationDate) >= to &&
    Number.isFinite(Number(a.sizeSf)) && Number(a.sizeSf) > 0
  )) return overlaps.reduce((sum, a) => sum + Number(a.sizeSf), 0);
  return null;
}

export function leaseAnnualHeadlines(lease, computed = lease?.computed, year = new Date().getFullYear(), asOf = new Date()) {
  const schedule = lease?.rentSchedule;
  const row = schedule?.find((r) => Number(r.year) === year && !r.isProjected && !r.isFallback);
  const hasSchedule = Array.isArray(schedule);
  const known = (value) => value == null || !Number.isFinite(Number(value)) ? null : Number(value);
  const contractual = hasSchedule
    ? known(row?.contractual)
    // annualRent is the exact-year source alias in older responses; the old
    // annualRentRunRate key may still contain a grossed-up value.
    : known(computed?.annualRent);
  const net = hasSchedule
    ? known(row?.net)
    : known(computed && Object.hasOwn(computed, "annualRentBilled")
      ? computed.annualRentBilled : computed?.annualRent);
  const area = annualAreaBasis(lease, year, row) ?? (
    !hasSchedule && computed?.annualAreaYear === year && Number(computed?.annualAreaSqft) > 0
      ? Number(computed.annualAreaSqft) : null
  );
  const currency = row?.currency || lease?.currency || "USD";
  const yearCost = lease?.yearCosts?.find((yc) => Number(yc.year) === year && !yc.isProjected);
  const monthlyAnchor = hasSchedule
    ? row && !row.isNotInSource ? row.contractual : null
    : yearCost && Object.hasOwn(yearCost, "rent") ? yearCost.rent
      : computed?.annualAreaYear === year && !computed?.isDerived ? computed?.annualRent : null;
  const monthlyDisplay = leaseMonthlyDisplay(lease?.areas, year, asOf.getMonth() + 1,
    lease?.currency || "USD", monthlyAnchor, hasSchedule ? currency : yearCost?.currency || currency, lease);
  return {
    contractual, net, currency, contractualCurrency: row?.contractualCurrency || currency,
    annual: row?.basis === "monthly_actual" ? net : contractual,
    basis: row?.basis ?? null,
    monthly: monthlyDisplay.amount, monthlyDisplay,
    area,
    contractualPsf: contractual != null && area != null ? contractual / area : null,
    netPsf: net != null && area != null ? net / area : null,
  };
}

// List rent may deliberately refer to an expired lease's historical year.
// Never borrow today's area or another year's annual denominator.
export function listAnnualArea(lease) {
  const year = lease?.listDisplayRentYear ?? lease?.annualRentYear;
  if (year == null) return null;
  if (lease?.listAnnualAreaYear === year && Number(lease.listAnnualAreaSqft) > 0) {
    return Number(lease.listAnnualAreaSqft);
  }
  if (lease?.annualAreaYear === year && Number(lease.annualAreaSqft) > 0) {
    return Number(lease.annualAreaSqft);
  }
  return annualAreaBasis(lease, year, { months: 12, isFallback:
    lease?.listDisplayRentYear != null ? lease?.isListDisplayRentDerived : lease?.isAnnualRentDerived });
}

export function annualRentArea(lease) {
  const year = lease?.annualRentYear;
  if (year == null) return null;
  if (lease?.annualAreaYear === year && Number(lease?.annualAreaSqft) > 0) return Number(lease.annualAreaSqft);
  return annualAreaBasis(lease, year, { months: 12, isFallback: lease?.isAnnualRentDerived });
}
